import {
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useNavigate,
} from "react-router-dom";
import Home from "./components/Home";
import PreLoginPage from "./components/PreLoginPage";
import Header from "./components/Header";
import { ROUTE } from "./constants";
import { CIAMAuthProvider } from "@ciam/ciam-kit";
import getCurrentEnv from "./utils/getCurrentEnv";
import { lazy, Suspense, useState } from "react";
import PublicHeader from "./components/PublicHeader";
import Redirect from "./components/Redirect";
import { getLoginUrl } from "./utils/getLoginUrl";
import usePageViewLogger from "./hooks/usePageViewLogger";
import useDocumentTitle from "./hooks/useDocumentTitle";
import DisclosureModal from "./components/DisclosureModal";
import HelpCenter from "./components/HelpCenter";
import msalInstance from "./auth/msalInstance";
import SSOAuthProvider from "./auth/SSOAuthProvider";
import userStore from "./store/userStore";
import clearAppSession from "./utils/clearAppSession";
import resolveSessionFootprint from "./utils/resolveSessionFootprint";

const PowerbiDashboard = lazy(() => import("./components/PowerbiDashboard"));

const PublicLayout = () => {
  useDocumentTitle();
  return (
    <>
      <DisclosureModal />
      <PublicHeader />
      <main>
        <Outlet />
      </main>
    </>
  );
};

const ProtectedLayout = () => {
  useDocumentTitle();
  usePageViewLogger();
  const env = getCurrentEnv();
  const navigate = useNavigate();
  const { setTokenReady } = userStore();

  // Mechanism is host-only (isInternalUser): internal => SSO, external => CIAM.
  const { mechanism } = resolveSessionFootprint({
    msalInstance,
  });


  const config = {
    env: env,
    appId: "insights",
    appHome: "/home",
    debugMode: false, // for localhost testing or development
    appLogin: getLoginUrl(env),
    refreshCallBack: (jwtToken) => {
      sessionStorage.setItem("jwtToken", jwtToken);
      setTokenReady(true);
    },
    logoutCallBack: () => {
      clearAppSession();
      setTokenReady(false);
      navigate(ROUTE.PRE_LOGIN);
    },
    initCallBack: (ciamSession) => {
      // The kit calls this on mount after resolving the HTTP-only cookie. A
      // session with a jwtToken means the user is authenticated; no token means
      // there is genuinely no CIAM session, so send them to PRE_LOGIN instead of
      // storing an "undefined" token and letting Home fire unauthenticated calls.
      if (ciamSession?.jwtToken) {
        sessionStorage.setItem("jwtToken", ciamSession.jwtToken);
        setTokenReady(true);
      } else {
        clearAppSession();
        setTokenReady(false);
        navigate(ROUTE.PRE_LOGIN);
      }
    },
  };

  const content = (
    <>
      <DisclosureModal />
      <Header />
      <main>
        <Outlet />
      </main>
    </>
  );

  if (mechanism === "sso") {
    // SSO always mounts; SSOAuthProvider self-resolves silent revival vs PRE_LOGIN.
    return (
      <SSOAuthProvider msalInstance={msalInstance}>{content}</SSOAuthProvider>
    );
  }

  // External/CIAM: always mount the provider. The CIAM session lives in an
  // HTTP-only cookie that only CIAMAuthProvider can resolve (via cookieResolve
  // on mount), which then fires initCallBack to populate sessionStorage.jwtToken.
  // Gating the mount on a pre-existing jwtToken deadlocked the fresh-login
  // return leg (no token yet -> never mounted -> token never written -> bounced
  // to PRE_LOGIN). The kit self-redirects to appLogin when there is genuinely no
  // session, and Home/PowerbiDashboard guard their fetches on tokenReady.
  return <CIAMAuthProvider config={config}>{content}</CIAMAuthProvider>;
};

const appRoutes = [
  {
    element: <PublicLayout />,
    children: [
      {
        path: ROUTE.PRE_LOGIN,
        element: <PreLoginPage />,
      },
    ],
  },
  {
    element: <ProtectedLayout />,
    children: [
      {
        path: ROUTE.HOME,
        element: <Home />,
      },
      {
          path: ROUTE.HELP_CENTER,
          element: <HelpCenter />,
      },
      {
        path: ROUTE.DASHBOARD,
        element: (
          <Suspense fallback={null}>
            <PowerbiDashboard />
          </Suspense>
        ),
      },
      {
        path: ROUTE.REDIRECT,
        element: <Redirect />,
      },
    ],
  },
];

const App = () => {
  // Create router lazily at render time (not module scope) so it reads
  // window.location AFTER index.js has processed the MSAL redirect and
  // called replaceState to set the correct URL.
  const [router] = useState(() => createBrowserRouter(appRoutes));
  return <RouterProvider router={router} />;
};

export default App;
